import { FC, KeyboardEvent, useEffect, useRef } from 'react'
import { Send } from 'lucide-react'
import { useIntl } from 'react-intl'

type Props = {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  disabled: boolean
}

const ChatComposer: FC<Props> = ({ value, onChange, onSend, disabled }) => {
  const intl = useIntl()
  const ref = useRef<HTMLTextAreaElement>(null)
  const canSend = !disabled && value.trim().length > 0

  // Auto-grow from the CSS min-height up to the CSS max-height.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    const min = parseFloat(getComputedStyle(el).minHeight) || 0
    el.style.height = `${Math.max(el.scrollHeight, min)}px`
  }, [value])

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (canSend) onSend()
    }
  }

  return (
    <div className='lum-composer'>
      <textarea
        ref={ref}
        className='lum-input'
        rows={1}
        /*
         * `auto` only once there is text: re-evaluated by the browser as you
         * type, so the field flips the moment the first strong character
         * lands. Without it, composing Persian in the English UI reads back
         * mirrored against the bubble it is about to become.
         *
         * Undefined while empty, because on a form control `dir='auto'`
         * resolves against the VALUE and the spec makes an empty one fall back
         * to `ltr` — the placeholder is never consulted. Unconditional `auto`
         * therefore rendered the Persian placeholder left-to-right in the
         * Persian UI, trailing ellipsis at the wrong end. Dropping the
         * attribute inherits the locale's direction from `<html>`, which is
         * the right basis for chrome the user did not write.
         */
        dir={value.trim() ? 'auto' : undefined}
        value={value}
        disabled={disabled}
        placeholder={intl.formatMessage({ id: 'Ask anything about this finding…' })}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <button
        type='button'
        className='lum-send'
        aria-label={intl.formatMessage({ id: 'Send' })}
        disabled={!canSend}
        onClick={onSend}
      >
        <Send size={16} aria-hidden='true' />
      </button>
    </div>
  )
}

export default ChatComposer
